import React from 'react';
import {cn} from '../../lib/utils';

interface FlexProps extends React.HTMLAttributes<HTMLDivElement> {
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
  gap?: number | string;
  height?: number | string;
  wrap?: 'wrap' | 'nowrap' | 'wrap-reverse';
  flex?: string;
  grow?: boolean;
  shrink?: boolean;
  basis?: string;
  className?: string;
  children?: React.ReactNode;
}

const Flex = React.forwardRef<HTMLDivElement, FlexProps>(
  (
    {
      direction = 'row',
      align = 'start',
      justify = 'start',
      height,
      gap,
      wrap = 'nowrap',
      flex,
      grow = false,
      shrink = false,
      basis,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const gapValue = typeof gap === 'number' ? `${gap * 0.25}rem` : gap;
    const heightValue = typeof height === 'number' ? `${height}px` : height;

    const classes = cn(
      'flex',
      {
        'flex-row': direction === 'row',
        'flex-col': direction === 'column',
        'flex-row-reverse': direction === 'row-reverse',
        'flex-col-reverse': direction === 'column-reverse',
        'items-start': align === 'start',
        'items-center': align === 'center',
        'items-end': align === 'end',
        'items-stretch': align === 'stretch',
        'items-baseline': align === 'baseline',
        'justify-start': justify === 'start',
        'justify-center': justify === 'center',
        'justify-end': justify === 'end',
        'justify-between': justify === 'between',
        'justify-around': justify === 'around',
        'justify-evenly': justify === 'evenly',
        'flex-wrap': wrap === 'wrap',
        'flex-nowrap': wrap === 'nowrap',
        'flex-wrap-reverse': wrap === 'wrap-reverse',
        'flex-grow': grow,
        'flex-shrink': shrink
      },
      className
    );

    const style: React.CSSProperties = {
      gap: gapValue,
      flex,
      flexBasis: basis,
      ...(height && {height: heightValue})
    };

    return (
      <div ref={ref} className={classes} style={style} {...props}>
        {children}
      </div>
    );
  }
);

Flex.displayName = 'Flex';

export {Flex};
